import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings, BarChart3, Shield, Building, Mail } from "lucide-react";

export default function CookiePolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "March 2, 2026";

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#121212] text-white py-6 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
              <span className="text-[#121212] font-bold text-xl">F</span>
            </div>
            <span className="text-2xl font-bold font-['Manrope']">FinAI</span>
          </Link>
          <nav className="flex items-center gap-6 text-sm">
            <Link to="/privacy" className="hover:text-[#BFFF00]">Privacy</Link>
            <Link to="/terms" className="hover:text-[#BFFF00]">Terms</Link>
            <Link to="/login" className="bg-[#BFFF00] text-[#121212] px-4 py-2 rounded-full font-medium hover:bg-[#a8e600]">Login</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-b from-[#F7FFE5] to-white py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-[#BFFF00] rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Cookie className="w-8 h-8 text-[#121212]" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-[#121212] font-['Manrope'] mb-4">Cookie Policy</h1>
          <p className="text-gray-600 text-lg">Last Updated: {lastUpdated}</p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-lg max-w-none">
          
          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4">What Are Cookies?</h2>
            <p className="text-gray-700 leading-relaxed">
              Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences, keeping you logged in, and analyzing how you use our platform.
            </p>
          </section>

          {/* Types of Cookies */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <Settings className="w-6 h-6 text-[#BFFF00]" />
              Types of Cookies We Use
            </h2>
            
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                <h3 className="font-semibold text-[#121212] mb-2">Essential Cookies (Required)</h3>
                <p className="text-gray-600 text-sm mb-3">
                  These cookies are necessary for the website to function and cannot be disabled.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <strong>Authentication:</strong> Keeps you logged into your account</li>
                  <li>• <strong>Security:</strong> Protects against CSRF attacks and maintains session integrity</li>
                  <li>• <strong>Preferences:</strong> Remembers your language and display settings</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-[#121212]">Analytics Cookies</h3>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">Optional</span>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  Help us understand how visitors interact with our website.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Page views and navigation patterns</li>
                  <li>• Time spent on pages</li>
                  <li>• Error tracking and performance monitoring</li>
                </ul>
              </div>
              
              <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-[#121212]">Functional Cookies</h3>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">Optional</span>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  Enable enhanced functionality and personalization.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Remember your dashboard layout preferences</li>
                  <li>• Store your recent searches and filters</li>
                  <li>• Enable chat support features</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Cookie Details */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-[#BFFF00]" />
              Specific Cookies We Use
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 rounded-xl overflow-hidden">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left font-semibold text-[#121212]">Cookie Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-[#121212]">Purpose</th>
                    <th className="px-4 py-3 text-left font-semibold text-[#121212]">Duration</th>
                    <th className="px-4 py-3 text-left font-semibold text-[#121212]">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">auth_token</td>
                    <td className="px-4 py-3 text-gray-600">User authentication</td>
                    <td className="px-4 py-3 text-gray-600">7 days</td>
                    <td className="px-4 py-3"><span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Essential</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">session_id</td>
                    <td className="px-4 py-3 text-gray-600">Session management</td>
                    <td className="px-4 py-3 text-gray-600">Session</td>
                    <td className="px-4 py-3"><span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Essential</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">csrf_token</td>
                    <td className="px-4 py-3 text-gray-600">Security protection</td>
                    <td className="px-4 py-3 text-gray-600">Session</td>
                    <td className="px-4 py-3"><span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Essential</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">preferences</td>
                    <td className="px-4 py-3 text-gray-600">User preferences</td>
                    <td className="px-4 py-3 text-gray-600">1 year</td>
                    <td className="px-4 py-3"><span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Functional</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">_ga</td>
                    <td className="px-4 py-3 text-gray-600">Google Analytics</td>
                    <td className="px-4 py-3 text-gray-600">2 years</td>
                    <td className="px-4 py-3"><span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Analytics</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Managing Cookies */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#BFFF00]" />
              Managing Your Cookie Preferences
            </h2>
            
            <p className="text-gray-700 mb-4">You have several options for managing cookies:</p>
            
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="font-semibold text-[#121212] mb-2">Browser Settings</h4>
                <p className="text-sm text-gray-600">
                  Most browsers allow you to control cookies through their settings. You can typically find these options in your browser's "Privacy" or "Security" settings.
                </p>
              </div>
              
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="font-semibold text-[#121212] mb-2">Our Cookie Banner</h4>
                <p className="text-sm text-gray-600">
                  When you first visit our site, you can choose which optional cookies to accept via our cookie consent banner.
                </p>
              </div>
              
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="font-semibold text-[#121212] mb-2">Opt-Out Links</h4>
                <p className="text-sm text-gray-600">
                  For analytics cookies, you can use Google's opt-out tool: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Analytics Opt-out</a>
                </p>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-6">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Disabling essential cookies will prevent you from using key features of our platform, including logging into your account.
              </p>
            </div>
          </section>

          {/* Third-Party Cookies */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4">Third-Party Cookies</h2>
            <p className="text-gray-700 mb-4">
              Some cookies on our site are set by third-party services we use:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Stripe:</strong> For secure payment processing</li>
              <li><strong>Plaid:</strong> For bank account connection functionality</li>
              <li><strong>Google Analytics:</strong> For website analytics (optional)</li>
            </ul>
            <p className="text-gray-700 mt-4">
              These third parties have their own privacy policies governing the use of their cookies.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <Mail className="w-6 h-6 text-[#BFFF00]" />
              Contact Us
            </h2>
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <Building className="w-6 h-6 text-[#121212] mt-1" />
                <div>
                  <p className="font-semibold text-[#121212]">Revurge Corporation</p>
                  <p className="text-gray-600">8 The Green, Dover, DE 19901</p>
                  <p className="text-gray-600 mt-2">
                    <strong>Email:</strong> <a href="mailto:info@revurge.com" className="text-blue-600 hover:underline">info@revurge.com</a>
                  </p>
                </div>
              </div>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#121212] text-white py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
            <Link to="/privacy" className="hover:text-[#BFFF00]">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#BFFF00]">Terms of Service</Link>
            <Link to="/data-use" className="hover:text-[#BFFF00]">Data Use Policy</Link>
            <Link to="/cookies" className="hover:text-[#BFFF00]">Cookie Policy</Link>
            <Link to="/security" className="hover:text-[#BFFF00]">Security</Link>
            <Link to="/faq" className="hover:text-[#BFFF00]">FAQ</Link>
          </div>
          <p className="text-center text-gray-500 text-sm mt-6">
            © {new Date().getFullYear()} Revurge Corporation. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
